import type { ReactNode } from 'react';

import type { Severity } from '@smartapply/shared';

interface MarginNoteProps {
  severity: Severity;
  label: string;
  children: ReactNode;
}

/**
 * Signature: anotação de margem estilo Tufte CSS / livro acadêmico.
 *
 * Em wide viewport (≥1024px), o componente pai (ex: FindingCard) coloca a
 * MarginNote em uma coluna lateral à direita do conteúdo principal, criando
 * o efeito clássico de "anotação na margem".
 *
 * Em mobile/tablet, fica abaixo, ainda com identidade visual (linha lateral
 * colorida + label em mono + bullet point conectando à severity).
 */
export function MarginNote({ severity, label, children }: MarginNoteProps) {
  const accent = accentOf(severity);

  return (
    <aside
      className="relative pl-4"
      style={{ borderLeft: `1px solid ${accent}` }}
    >
      <div
        aria-hidden
        className="absolute -left-[4px] top-0 h-2 w-2 rounded-full"
        style={{ backgroundColor: accent }}
      />
      <p
        className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]"
        style={{ color: accent }}
      >
        {label}
      </p>
      <div className="mt-2 font-serif text-[14px] leading-[1.55] text-[var(--ink-primary)]">
        {children}
      </div>
    </aside>
  );
}

function accentOf(severity: Severity): string {
  switch (severity) {
    case 'critical':
      return 'var(--accent-crimson)';
    case 'suggestion':
      return 'var(--accent-saffron)';
    case 'positive':
      return 'var(--accent-forest)';
  }
}
